import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatSidenav, MatSidenavContent } from '@angular/material/sidenav';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { EditSidenavComponent } from '../../components/edits/edit-sidenav/edit-sidenav.component';
import { INotifyMessage } from '../../components/side-nav/notify/notify-messages.interface';
import { NotifyService } from '../../components/side-nav/notify/notify.service';
import { SearchHistoryService } from '../../components/side-nav/search-history/search-history.service';
import { ColorComponent } from '../../components/ui/color/color.component';
import { DashboardService } from '../../pages/dashboard/dashboard.service';
import { HeaderComponent } from '../../pages/header/header.component';
import { AuthService, UserData, SYSTEMCATEGORY } from '../../services/auth/auth.service';
import { ColorsService } from '../../services/colors/colors.service';
import { ConfigService } from '../../services/config/config.service';
import { SharedService, StaffIframe } from '../../services/shared/shared.service';
import { StorageService } from '../../services/storage/storage.service';
import * as $ from 'jquery';
import { LayoutService } from '../../services/layout.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { SessionExpiryDialog } from '../../pages/header/dialogs/session-expiry-dialog/session-expiry-dialog.component';
import {
  LayoutSettings,
  Page,
  PredefinedTemplatesService,
} from '../../services/edit/predefined-templates.service';
import { OpacSettingsService } from '../../services/opac-settings/opac-settings.service';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { state, style, transition, animate, trigger } from '@angular/animations';
import { RoutingService } from '../../services/routing/routing.service';

export interface MenuItem {
  type: 'home' | 'staff-dashboard' | 'search-history' | 'blank-ill-request' | 'web-links' | 'page';
  label?: string;
  icon?: string;
  page?: Page;
}

@Component({
  selector: 'ag-main-v6-layout',
  templateUrl: './main-v6-layout.component.html',
  styleUrls: ['./main-v6-layout.component.scss'],
  animations: [
    trigger('fadeInOut', [
      state(
        'void',
        style({
          opacity: 0,
        }),
      ),
      transition('void <=> *', animate(250)),
    ]),
  ],
})
// v6 layout which was once inside of the AppComponent
export class MainV6LayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('header') header: HeaderComponent;
  @ViewChild('pageContainer') mainContent: ElementRef<HTMLElement>;
  @ViewChild('staffPageContainer') mainStaffContent: ElementRef<HTMLElement>;
  @ViewChild('footer') footer: ElementRef<HTMLElement>;
  @ViewChild('color') color: ColorComponent;
  @ViewChild('pages') pages: EditSidenavComponent;
  @ViewChild('sideNav') sideNav: MatSidenav;
  @ViewChild(MatSidenavContent) sideNavContent: MatSidenavContent;

  a11y = false;

  bodyPadding = true;
  hideHeader: boolean;
  hideFooter: boolean;
  showCopyRights: boolean;
  showBackToTop: boolean;

  menuItems: MenuItem[];
  // TODO: Change use to MenuItem[]
  activePages: Page[];

  locations: any;

  showLibraryCard: boolean;
  libraryCardId = '';
  libraryCardLibraryName = '';
  trigger: any = Date.now();

  kidsBGColor = false;

  homeLocationLink: any;
  searchHistory: any;

  staffMenuItem: any;

  numberOfTargets = 0;

  notifications: INotifyMessage;

  DATE = new Date();

  private _autoLogoutTimer: any;
  private _accessTabs = ['skip2Search', 'skip2Content', 'skip2Nav'];
  private _mainTab = false;
  private _navTab = false;
  private _searchTab = false;
  private _destroyed$ = new Subject<void>();

  constructor(
    private _cd: ChangeDetectorRef,
    private _dialog: MatDialog,
    private _router: Router,
    private _authService: AuthService,
    private _colorsService: ColorsService,
    private _configService: ConfigService,
    private _dashboardService: DashboardService,
    public layoutService: LayoutService,
    private _notifyService: NotifyService,
    private _predefinedTemplatesService: PredefinedTemplatesService,
    private _searchHistoryService: SearchHistoryService,
    public sharedService: SharedService,
    private _storageService: StorageService,
    private _translationService: TranslateService,
    private _opacSettingsService: OpacSettingsService,
    private _routingService: RoutingService,
  ) {
    _translationService.setDefaultLang('en');

    // track browser back
    // https://stackoverflow.com/questions/40381814/how-do-i-detect-user-navigating-back-in-angular2
    this._patronTimeoutExpiryHandler = this._patronTimeoutExpiryHandler.bind(this);
  }

  ngOnInit(): void {
    this._initializeSubscriptions();
    this.hideComponentsBasedOnRoute();
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  hideComponentsBasedOnRoute() {
    this.showCopyRights = this._router.url.includes('/dashboard');

    const hideHeaderRoutes = ['/PatronLookupTool', '/shippingLabels/print'];
    this.hideHeader = hideHeaderRoutes.some((route) => this._router.url.includes(route));

    const hideFooterRoutes = [
      '/dashboard',
      '/configuration',
      '/marc',
      '/PatronLookupTool',
      '/reports',
      '/staff',
      '/shippingLabels',
    ];
    this.hideFooter = hideFooterRoutes.some((route) => this._router.url.includes(route));

    this.showBackToTop = !this._router.url.includes('/details');
  }

  ngAfterViewInit(): void {
    if (this.footer.nativeElement.offsetHeight !== 0) {
      this.layoutService.setFooterHeight(this.footer.nativeElement.offsetHeight);
    }

    const footerObserver = new ResizeObserver((entries) => {
      const height = entries[0].contentRect.height;
      this.layoutService.setFooterHeight(height);
    });
    footerObserver.observe(this.footer.nativeElement);

    const sidenavObserver = new ResizeObserver((entries) => {
      const scrollbarWidth = entries[0].target.offsetWidth - entries[0].target.clientWidth;
      this.layoutService.setContentScrollbarWidth(scrollbarWidth);
    });
    sidenavObserver.observe(this.sideNavContent.getElementRef().nativeElement);

    // Needed for Header / Footer height changes.
    this._cd.detectChanges();
    this.layoutService.setSideNav(this.sideNav);
    this.layoutService.setContentComponent(this.sideNavContent);
  }

  // ON EVERY ROUTE DO SOME ROUTINE CHECKS TO NORMALIZE THE ROUTER.
  onRouteActivate(event) {
    document.getElementById('app-body').scrollTop = 0;
    document.body.classList.remove('fixed');
  }

  private _getNotifications() {
    this._notifyService.getNotificationMessages().subscribe((notifications) => {
      this.notifications = notifications;
    });
  }

  closeLibraryCard() {
    this.showLibraryCard = false;
    document.body.style.removeProperty('overflow');
    document.body.style.top = '0px';
    document.body.style.position = 'relative';
  }

  openLibraryCard(data: any) {
    if (data) {
      this.showLibraryCard = data.showLibraryCard;
      this.libraryCardId = data.libraryCardId;
      this.libraryCardLibraryName = data.libraryCardLibraryName;
      if (data.showLibraryCard) {
        document.body.style.overflow = 'hidden';
        document.body.style.removeProperty('top');
        document.body.style.removeProperty('position');
      }
    }
  }

  private _initializeSubscriptions(): void {
    this._router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.closeIframesAndSideNavs();
        this.hideComponentsBasedOnRoute();
        this.sideNavContent.scrollTo({ top: 0 });
      }
    });

    // SideNav Template Changes
    // Can this be move to the templates OnInit?
    this.layoutService.sideNavConfig$.pipe(takeUntil(this._destroyed$)).subscribe((config) => {
      if (!config) {
        return;
      }
      // Needed so that some sidenav components refresh
      if (this._router.url.includes('dashboard')) {
        this.trigger = Date.now();
      }
      switch (config.template) {
        case 'notify':
          this._getNotifications();
          break;
        case 'history':
          this._getSearchHistory();
          break;
        case 'addList':
          this.sharedService.setLoadGuestListBS(true); // listner in your list
          this.closeOverlay();
          break;
      }
    });

    // UserData changes
    this.sharedService.userData$.pipe(takeUntil(this._destroyed$)).subscribe((userData) => {
      if (!userData) {
        return;
      }
      this._enablePatronTimeoutExpiry();
    });

    this.layoutService.layoutSettings$
      .pipe(takeUntil(this._destroyed$))
      .subscribe((layoutSettings: LayoutSettings) => {
        if (!layoutSettings) {
          this.resetColor(); // remove colors and styles for current library with resetColor
          return;
        }
        // Add color theme.
        if (layoutSettings.styles.length > 0) {
          this.updateColor(layoutSettings.styles[0]);
        } else {
          this.resetColor();
        }
      });

    // Page Links
    combineLatest([
      this._predefinedTemplatesService.activePages$,
      this._opacSettingsService.getWebLinkOptionsBS(),
    ]).subscribe(([activePages, weblinks]) => {
      if (!activePages) {
        return;
      }

      this.activePages = activePages;
      this._updateMenuItems(weblinks?.webLinkTabLabel);
    });

    this.sharedService.openStaffPage$.pipe(takeUntil(this._destroyed$)).subscribe((menuItem) => {
      this.staffMenuItem = menuItem;
    });

    // iframe pop url data from search
    this.sharedService.staffIframe$
      .pipe(takeUntil(this._destroyed$))
      .subscribe((response: StaffIframe) => {
        if (!response) {
          return;
        }

        this.openStaffUrlInNewTab(response.targetUrl, response.target, response.windowFeatures);
      });

    // EDIT Page
    this.sharedService.getPageSlideNav().subscribe((value) => {
      if (!value) {
        return;
      }

      this.addPage(value);
    });
    // ADD - REMOVE ROW.
    this.sharedService.getRowSlideNav().subscribe((value) => {
      if (!value) {
        return;
      }

      this.editRow(value);
    });
  }

  setContentScrollOffset(event: Event) {
    this.layoutService.setContentScrollOffset((event.target as HTMLElement).scrollTop);
  }

  /**
   * Builds the Menu-Items based on defined pages and available pages
   */
  private _updateMenuItems(webLinkLabel = 'Web Links') {
    this.menuItems = [{ type: 'home', icon: 'fa fa-home fa-lg' }];
    const userData = this._authService.getUserDataFromSession();
    if (userData) {
      if (userData.isStaff) {
        this.menuItems.push({ type: 'staff-dashboard', label: 'Staff Dashboard' });
      }
      if (!userData.isGuest) {
        this.menuItems.push({ type: 'search-history', label: 'Search History' });
      }
      if (userData.illOptions.showBlankILL) {
        this.menuItems.push({ type: 'blank-ill-request', label: 'Blank ILL Request' });
      }
      if (userData.opacOptions?.showWebLinkOnSeparateTab) {
        this.menuItems.push({ type: 'web-links', label: webLinkLabel });
      }
    }

    if (this.activePages) {
      const activeMenuItems = this.activePages
        ?.filter((p) => this._showMenuItem(p, userData))
        .map(
          (p) =>
            <MenuItem>{
              type: 'page',
              label: p.label,
              page: p,
            },
        );
      // Remove first non-link page
      if (activeMenuItems.length > 0 && !activeMenuItems[0].page.linkPage) {
        activeMenuItems.shift();
      }
      this.menuItems = [...this.menuItems, ...activeMenuItems];
    }
  }

  /**
   * Checks page permission for the current user
   * @param page
   * @param userData
   * @returns
   */
  private _showMenuItem(page: Page, userData: UserData) {
    let show = false;
    if (userData) {
      if (userData.isGuest) {
        show = page.showGuest;
      } else if (userData.isPatron) {
        show = page.showPatron;
      } else if (userData.isStaff) {
        show = page.showStaff;
      }
    }
    return show;
  }

  setPosition(side) {
    this.trigger = Date.now();
    this._openSideNav(side.template, side.position);

    this.closeOverlay();
    setTimeout(() => {
      this.sideNav?.open();
    }, 200);
  }

  openPage(event, data) {
    this._openSideNav('page', 'end', data);
  }

  addPage(data) {
    this._openSideNav('pages', 'start', data);
  }

  trackingSummary(data) {
    this._openSideNav('tracking', 'end', data);
  }

  editRow(data) {
    this._openSideNav('row', 'start', data);
  }

  private _openSideNav(template: string, position: 'start' | 'end', data?, mode?) {
    this.layoutService.openSideNav({ template, position, data, mode });
  }

  updateColor(style) {
    this.removeColors();
    // Primary Colors
    document.documentElement.style.setProperty('--shade-base', style.shadeBase);
    document.documentElement.style.setProperty('--shade-one', style.shadeOne);
    document.documentElement.style.setProperty('--shade-two', style.shadeTwo);
    document.documentElement.style.setProperty('--shade-three', style.shadeThree);
    document.documentElement.style.setProperty('--shade-four', style.shadeFour);
    document.documentElement.style.setProperty('--neutral', style.neutral);
    document.documentElement.style.setProperty('--offset-one', style.offsetOne);
    document.documentElement.style.setProperty('--offset-two', style.offsetTwo);
    document.documentElement.style.setProperty('--offset-three', style.offsetThree);
    document.documentElement.style.setProperty('--offset-four', style.offsetFour);
    document.documentElement.style.setProperty('--accent-one', style.accentOne);
    document.documentElement.style.setProperty('--accent-two', style.accentTwo);
    document.documentElement.style.setProperty('--accent-three', style.accentThree);
    document.documentElement.style.setProperty('--accent-four', style.accentFour);
    document.documentElement.style.setProperty('--link', style.link);
    // Starting Variables.
    document.documentElement.style.setProperty('--header-background', style.headerBackground);
    document.documentElement.style.setProperty('--header-color', style.headerColor);
    document.documentElement.style.setProperty('--footer-background', style.footerBackground);
    document.documentElement.style.setProperty('--footer-color', style.footerColor);
    document.documentElement.style.setProperty('--body-color', style.bodyColor);
    document.documentElement.style.setProperty('--body-background', style.bodyBackground);

    document.documentElement.style.setProperty('--button-background', style.buttonBackground);
    document.documentElement.style.setProperty('--button-border', style.buttonBorder);
    document.documentElement.style.setProperty('--button-color', style.buttonColor);
    if (style.typeOf && style.typeOf != null) {
      document.body.classList.add(style.typeOf);
    }
  }

  resetColor() {
    this.removeColors();
    this._colorsService.setColorDefaultsBS(true);
    this._colorsService.setColorPalleteBS(true);
  }

  removeColors() {
    //
    // Color Theme
    //
    document.documentElement.style.removeProperty('--shade-base');
    document.documentElement.style.removeProperty('--shade-one');
    document.documentElement.style.removeProperty('--shade-two');
    document.documentElement.style.removeProperty('--shade-three');
    document.documentElement.style.removeProperty('--shade-four');
    document.documentElement.style.removeProperty('--neutral'); // for custom, material, and bootstrap.
    document.documentElement.style.removeProperty('--offset-one');
    document.documentElement.style.removeProperty('--offset-two');
    document.documentElement.style.removeProperty('--offset-three'); // text-icons
    document.documentElement.style.removeProperty('--offset-four');
    document.documentElement.style.removeProperty('--accent-one'); // text-icons
    document.documentElement.style.removeProperty('--accent-two');
    document.documentElement.style.removeProperty('--accent-three');
    document.documentElement.style.removeProperty('--accent-four'); // text-icons

    document.documentElement.style.removeProperty('--text-icons');
    document.documentElement.style.removeProperty('--primary-text');
    document.documentElement.style.removeProperty('--light');
    document.documentElement.style.removeProperty('--light-primary-color');
    document.documentElement.style.removeProperty('--dark');
    document.documentElement.style.removeProperty('--dark-primary-color');
    document.documentElement.style.removeProperty('--secondary-text');
    document.documentElement.style.removeProperty('--divider-color');
    document.documentElement.style.removeProperty('--warning');
    document.documentElement.style.removeProperty('--secondary');
    document.documentElement.style.removeProperty('--info');

    document.documentElement.style.removeProperty('--body-color');
    document.documentElement.style.removeProperty('--body-background');
    document.documentElement.style.removeProperty('--button-background');
    document.documentElement.style.removeProperty('--button-color');
    document.documentElement.style.removeProperty('--button-border');

    document.documentElement.style.removeProperty('--header-background'); // offset-four
    document.documentElement.style.removeProperty('--header-color'); // TEXT/ICONS
    document.documentElement.style.removeProperty('--footer-background'); // DARK PRIMARY COLOR
    document.documentElement.style.removeProperty('--footer-color'); // TEXT/ICONS
    document.documentElement.style.removeProperty('--box-shadows'); // dark
    document.documentElement.style.removeProperty('--drop-shadows'); // dark
    document.documentElement.style.removeProperty('--link'); //PRIMARY COLOR
    document.documentElement.style.removeProperty('--hover-focus'); //ACCENT COLOR
    document.documentElement.style.removeProperty('--primary'); // MAIN COLOR. DEFAULT FOR BUTTONS AS WELL.
    document.documentElement.style.removeProperty('--secondary'); // ACCENT COLOR
    document.documentElement.style.removeProperty('--info'); // LIGHT PRIMARY COLOR
    document.documentElement.style.removeProperty('--warning'); // DEEP ORANGE
    document.documentElement.style.removeProperty('--danger'); // DEEP RED
    document.documentElement.style.removeProperty('--light'); // SECONDARY TEXT
    document.documentElement.style.removeProperty('--dark'); // PRIMARY TEXT
    document.body.className = '';
  }

  closeIframesAndSideNavs() {
    this.sharedService.setOpenStaffPage(null);
    this.sideNav?.close();
    this.resetA11y();
  }

  //   ---------------------------------------------------------------------------
  //   --------------------------    A11y and HotKeys  ---------------------------
  //   ---------------------------------------------------------------------------
  resetA11y() {
    this.a11y = false;
    this._searchTab = false;
    this._mainTab = false;
    this._navTab = false;
    $('#skip2Content').blur();
    $('#skip2Search').blur();
    $('#skip2Nav').blur();
    $('main').removeClass('outline');
  }
  skip2Main() {
    const mainElement: HTMLElement = this.staffMenuItem
      ? this.mainStaffContent.nativeElement
      : this.mainContent.nativeElement;

    mainElement.focus();
  }
  skip2Search() {
    this._searchTab = true;
    this.header.searchInput.nativeElement.focus();
  }
  navigation(_event) {
    this.setPosition({ template: '', position: 'end' });
    this.a11y = true;
    this._navTab = true;
    $('#skip2Nav').blur();
  }
  shiftTabs(move, current) {
    // The TAB Functionality for A11y
    // Have NOT selected skip to : main content, search, navigation.
    if (!this._searchTab == false && this._mainTab == false && this._navTab == false) {
      if (current < -1 || current == this._accessTabs.length) {
        // MIN
        current = -1;
      }
      if (current == -1) {
        // reset by default.
        $('#skip2Content').blur();
        $('#skip2Search').blur();
        $('#skip2Nav').blur();
      } else {
        // set the current focus.
        $('#' + this._accessTabs[current]).focus();
      }
    }

    //
    // Navigation Menu
    //
    if (this._navTab == true) {
      //
      // find all tabindexes, build every tab to handle guest/patron/staff/ timeouts.
      //
      const count = [];
      $('#userNav [tabindex]').each(function () {
        count.push(this.id);
      });
      const currentFocus = document.activeElement;
      count.indexOf(currentFocus.id);
      //
      // set the next focus with the tab (foward) || shift+tab (backward)
      //
      let nextFocus = count.indexOf(currentFocus.id);
      if (move == 'shift+tab') {
        // do you want to go back, if not go forward.
        if (nextFocus == 0) {
          nextFocus = count.length - 1;
        } else {
          nextFocus = nextFocus - 1;
        }
      } else {
        if (nextFocus == count.length - 1) {
          nextFocus = 0;
        } else {
          nextFocus = nextFocus + 1;
        }
      }
      $('#' + currentFocus.id).blur();
      $('#' + count[nextFocus]).focus();
    }
    //
    // Search Content -- by route.
    //
    if (this._searchTab == true) {
      //
      // Find ALL tabindexes
      //
      const count = [];
      $('#search-overlay [tabindex]').each(function () {
        count.push(this.id);
      });
      const currentFocus = document.activeElement;
      count.indexOf(currentFocus.id);
    }
  }

  //   ---------------------------------------------------------------------------
  //   ------------------------------USER EXPERIENCE -----------------------------
  //   ---------------------------------------------------------------------------
  closeOverlay() {
    $('#search-overlay').css('display', 'none');
  }

  // TODO: Move to app component?
  private _getSearchHistory(): void {
    this._searchHistoryService.getSearchHistory().subscribe((response) => {
      if (response.status == 'OK') {
        this.searchHistory = response.data;
      }
    });
  }

  openStaffUrlInNewTab(
    targetUrl,
    target = '_blank',
    windowFeatures = 'location=yes,height=650,width=650,scrollbars=yes,status=yes',
  ) {
    if (targetUrl.startsWith('http') || targetUrl.startsWith('https')) {
      window.open(targetUrl, target, windowFeatures);
    } else {
      let origin = this._routingService.baseUri;
      if (this._configService.agApi.includes('localhost') && targetUrl.includes('loginmodule')) {
        origin = this._configService.agApi;
      } else if (this._routingService.baseUri.includes('localhost')) {
        SYSTEMCATEGORY.SHAREIT
          ? (origin = this._configService.baseUrl.replace(
              'api',
              `${this._storageService.getCid()}-agent`,
            ))
          : (origin = this._configService.baseUrl.replace(
              'api',
              `${this._storageService.getCid()}-verso`,
            ));
      }

      const url = origin + targetUrl;
      // https://stackoverflow.com/questions/14132122/open-url-in-new-window-with-javascript
      window.open(url, target, windowFeatures);
    }
  }

  /**
   * Enables Patron Session Timeout.
   * TODO: Move to LayoutService
   */
  private _enablePatronTimeoutExpiry(): void {
    if (this._autoLogoutTimer) {
      clearTimeout(this._autoLogoutTimer);
      this._autoLogoutTimer = undefined;
    }

    if (this._authService.isPatron() || this._authService.isGuest()) {
      document.addEventListener('mousemove', this._patronTimeoutExpiryHandler);
    } else {
      document.removeEventListener('mousemove', this._patronTimeoutExpiryHandler);
    }
  }

  private _patronTimeoutExpiryHandler() {
    if (this._autoLogoutTimer) {
      clearTimeout(this._autoLogoutTimer);
    }
    this._autoLogoutTimer = setTimeout(
      () => {
        const dialogRef = this._dialog.open(SessionExpiryDialog, {
          width: '600px',
          height: 'auto',
          disableClose: true,
        });
        dialogRef.afterClosed().subscribe((result) => {
          if (result === 'logout' || result === 'timeout') {
            this._authService.OnLogOut();
          }
        });
      },
      10 * 60 * 1000,
    );
  }
}
